/**
 * Comment Security & Validation Utilities
 * Handles XSS prevention, input validation, and content sanitization
 */

// Basic HTML sanitization without external dependencies
// TODO: Consider using DOMPurify for more sophisticated sanitization if we want to allow
// formatted text (bold, italic, links) in the future. Current implementation is safe
// but removes all formatting.
export function sanitizeContent(content: string): string {
  if (!content || typeof content !== "string") {
    return "";
  }

  // Remove all HTML tags and decode HTML entities
  return content
    .replace(/<[^>]*>/g, "") // Remove HTML tags
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, "/")
    .trim();
}

// Comprehensive input validation
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  sanitizedContent?: string;
}

export function validateCommentContent(content: string): ValidationResult {
  const errors: string[] = [];

  if (!content || typeof content !== "string") {
    errors.push("Content is required");
    return { isValid: false, errors };
  }

  const trimmedContent = content.trim();

  // Length validation
  if (trimmedContent.length === 0) {
    errors.push("Comment cannot be empty");
  }

  if (trimmedContent.length > 2000) {
    errors.push("Comment must be less than 2000 characters");
  }

  if (trimmedContent.length < 1) {
    errors.push("Comment must be at least 1 character long");
  }

  // Content validation
  if (/^\s*$/.test(trimmedContent)) {
    errors.push("Comment cannot be only whitespace");
  }

  // Basic spam detection patterns
  const spamPatterns = [
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g,
    /([a-zA-Z0-9])\1{8,}/g, // Repeated single character spam (more than 8 times) - excludes emojis
  ];

  for (const pattern of spamPatterns) {
    if (pattern.test(trimmedContent)) {
      errors.push("Comment contains prohibited content");
      break;
    }
  }

  const sanitizedContent = sanitizeContent(trimmedContent);

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedContent: errors.length === 0 ? sanitizedContent : undefined,
  };
}

// Rate limiting is now handled by the loggedInRouteWithRateLimit guard in the API route
// using Upstash Redis for proper distributed rate limiting.
// See /app/api/comments/route.ts for implementation
